import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Calendar, Wrench, Ship, User } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function BottomNav() {
  const loc = useLocation();
  const { t } = useLang();
  const { user } = useAuth();
  const [pending, setPending] = useState(0);

  // Fetch de mantenimientos "programado" asignados al usuario
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/mantenimientos/mine/pending-count");
        if (alive) setPending(data?.count || 0);
      } catch { /* silent */ }
    };
    load();
    const onRefresh = () => load();
    const onVisible = () => { if (!document.hidden) load(); };
    window.addEventListener("sns:refresh-pending", onRefresh);
    window.addEventListener("sns:push", onRefresh);
    document.addEventListener("visibilitychange", onVisible);
    // Cada 60s revalidamos por si el admin asigna algo mientras la app está abierta
    const iv = setInterval(load, 60000);
    return () => {
      alive = false;
      window.removeEventListener("sns:refresh-pending", onRefresh);
      window.removeEventListener("sns:push", onRefresh);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(iv);
    };
  }, [user, loc.pathname]);

  // Hide on detail pages
  const hide = /^\/(trabajos|embarcaciones)\/[^/]+/.test(loc.pathname) ||
               /^\/gestion/.test(loc.pathname) ||
               loc.pathname.startsWith("/nuevo") ||
               loc.pathname.startsWith("/editar") ||
               loc.pathname.startsWith("/mis-horas") ||
               loc.pathname.startsWith("/calendario");
  if (hide) return null;

  const TABS = [
    { to: "/", label: t("nav.dashboard"), Icon: Calendar, end: true, key: "inicio" },
    { to: "/trabajos", label: t("nav.trabajos"), Icon: Wrench, key: "trabajos" },
    { to: "/embarcaciones", label: t("nav.embarcaciones"), Icon: Ship, key: "embarcaciones" },
    { to: "/perfil", label: t("nav.perfil"), Icon: User, key: "perfil" },
  ];

  return (
    <nav
      className={`fixed bottom-0 left-0 right-0 z-40 pointer-events-none ${user?.role === "admin" ? "lg:hidden" : ""}`}
      data-testid="bottom-nav"
    >
      <div className="max-w-[440px] mx-auto pointer-events-auto bg-white border-t border-[var(--border)] pb-[env(safe-area-inset-bottom)] md:border-l md:border-r md:border-[var(--border)]">
        <div className="grid grid-cols-4 h-[72px]">
          {TABS.map(({ to, label, Icon, end, key }) => {
            const showBadge = key === "trabajos" && pending > 0;
            return (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 text-xs font-semibold transition-colors ${
                    isActive ? "text-[var(--coral)]" : "text-slate-400 hover:text-slate-600"
                  }`
                }
                data-testid={`nav-${key}`}
              >
                <div className="relative">
                  <Icon size={22} strokeWidth={2} />
                  {showBadge && (
                    <span
                      className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--coral)] text-white text-[10px] font-bold flex items-center justify-center leading-none shadow-md ring-2 ring-white"
                      data-testid="nav-trabajos-badge"
                    >
                      {pending > 9 ? "9+" : pending}
                    </span>
                  )}
                </div>
                <span className="truncate max-w-[72px]">{label}</span>
              </NavLink>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
